import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

import { MessageTextStyle } from '@/entities/hey-syncly';
import { STUDIO_ITEM_EXPORT_STORAGE_KEY } from '@/entities/studio';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import { StudioItemMarkdown } from '../components/StudioItemDetail/studio-item-markdown';
import type { HeySynclyStudioItem } from '../model';

export const STUDIO_ITEM_PRINT_STYLES = `
  @page {
    size: A4 portrait;
    margin: 12mm;
  }

  @media print {
    html,
    body,
    #root,
    #static-root {
      width: auto !important;
      height: auto !important;
      min-height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible !important;
    }

    #static-root {
      background: white !important;
    }

    #static-root .static-studio-item-content {
      display: block !important;
      width: 100% !important;
      max-width: none !important;
    }

    #static-root .static-studio-item-meta {
      font-size: 9pt;
      line-height: 1.4;
    }

    #static-root .static-studio-item-title {
      font-size: 22pt;
      line-height: 1.2;
      letter-spacing: -0.02em;
    }

    #static-root .static-studio-item-body {
      --font-size: 11pt;
      --table-font-size: 10.5pt;
      font-size: var(--font-size);
      line-height: 1.55;
    }

    #static-root .static-studio-item-body > :first-child {
      margin-top: 0;
    }

    #static-root .static-studio-item-body h1 {
      margin-top: 18pt;
      margin-bottom: 6pt;
      padding: 0;
      font-size: 18pt;
      line-height: 1.2;
    }

    #static-root .static-studio-item-body h2 {
      margin-top: 16pt;
      margin-bottom: 5pt;
      padding: 0;
      font-size: 15.5pt;
      line-height: 1.25;
    }

    #static-root .static-studio-item-body h3 {
      margin-top: 12pt;
      margin-bottom: 4pt;
      padding: 0;
      font-size: 13.5pt;
      line-height: 1.3;
    }

    #static-root .static-studio-item-body h4 {
      margin-top: 10pt;
      margin-bottom: 3pt;
      padding: 0;
      font-size: 12pt;
      line-height: 1.35;
    }

    #static-root .static-studio-item-body h5 {
      margin-top: 8pt;
      margin-bottom: 3pt;
      padding: 0;
      font-size: 11pt;
      line-height: 1.4;
    }

    #static-root .static-studio-item-body h6 {
      margin-top: 7pt;
      margin-bottom: 3pt;
      padding: 0;
      font-size: 10pt;
      line-height: 1.4;
    }

    #static-root header,
    #static-root h1,
    #static-root h2,
    #static-root h3,
    #static-root h4,
    #static-root h5,
    #static-root h6 {
      break-after: avoid-page;
      page-break-after: avoid;
    }

    #static-root p,
    #static-root li {
      orphans: 3;
      widows: 3;
    }

    #static-root .static-studio-item-body p,
    #static-root .static-studio-item-body li {
      line-height: 1.55;
    }

    #static-root table {
      display: table !important;
      width: 100% !important;
      max-width: 100% !important;
      table-layout: fixed;
      overflow: visible !important;
      font-size: var(--table-font-size);
      line-height: 1.4;
    }

    #static-root th,
    #static-root td {
      padding: 5pt 6pt;
      vertical-align: top;
    }

    #static-root thead {
      display: table-header-group;
    }

    #static-root tbody {
      display: table-row-group;
    }

    #static-root tr,
    #static-root pre,
    #static-root blockquote {
      break-inside: avoid-page;
      page-break-inside: avoid;
    }

    #static-root th,
    #static-root td,
    #static-root a,
    #static-root pre {
      min-width: 0 !important;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    #static-root pre {
      white-space: pre-wrap;
      overflow: visible !important;
      font-size: 9.5pt;
      line-height: 1.45;
    }

    #static-root img {
      max-width: 100% !important;
      max-height: 273mm;
      height: auto;
      object-fit: contain;
      break-inside: avoid-page;
      page-break-inside: avoid;
    }

    #static-root,
    #static-root * {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      forced-color-adjust: exact;
    }
  }
`;

const isStudioItem = (value: unknown): value is HeySynclyStudioItem => {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Partial<HeySynclyStudioItem>;

  return (
    typeof candidate.alias === 'string' &&
    typeof candidate.created_at === 'string' &&
    typeof candidate.created_by === 'string' &&
    typeof candidate.title === 'string' &&
    !!candidate.content &&
    typeof candidate.content === 'object' &&
    typeof candidate.content.body === 'string'
  );
};

const getPdfDocumentTitle = (title?: string) => {
  if (!title) return 'studio-item.pdf';

  const safeTitle = title
    .replace(/[<>:"/\\|?*]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return `${safeTitle || 'studio-item'}.pdf`;
};

const StaticStudioItemDetailPage = () => {
  const item = useMemo(() => {
    const storedItem = localStorage.getItem(STUDIO_ITEM_EXPORT_STORAGE_KEY);
    if (!storedItem) return null;

    try {
      const parsedItem: unknown = JSON.parse(storedItem);
      return isStudioItem(parsedItem) ? parsedItem : null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>{getPdfDocumentTitle(item?.title)}</title>
        <style type="text/css">{STUDIO_ITEM_PRINT_STYLES}</style>
      </Helmet>
      <div id="static-root" className="min-h-screen bg-white px-10 py-12 text-slate-900">
        <div className="static-studio-item-content mx-auto flex w-full max-w-4xl flex-col gap-8">
          {!item && (
            <div className="rounded-3xl border border-slate-200 px-6 py-8 text-sm text-slate-500">
              Export data not found.
            </div>
          )}
          {item && (
            <>
              <header className="border-b border-slate-200 pb-6">
                <p className="static-studio-item-meta text-sm font-medium text-slate-500">
                  {item.created_by} ·{' '}
                  {getTimezoneDayjs(item.created_at).format(DATE_FORMAT.readable)}
                </p>
                <h1 className="static-studio-item-title mt-3 text-4xl font-semibold tracking-tight text-slate-950">
                  {item.title}
                </h1>
              </header>
              <MessageTextStyle className="static-studio-item-body rounded-3xl p-2">
                <StudioItemMarkdown
                  content={item.content.body}
                  stripCategories={true}
                />
              </MessageTextStyle>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default StaticStudioItemDetailPage;
